import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { Globe, GraduationCap, Building2, Heart, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

// ─── Block 1: Partenaires Institutionnels ────────────────────────────────────
const institutionnels = [
  { name: 'MINMIDT', fullName: 'Ministère des Industries, des Mines et du Développement Technologique', logo: '/images/partners/minmidt.jpg' },
  { name: 'Palais des Congrès', fullName: 'Palais des Congrès de Yaoundé', logo: '/images/partners/palais-congres.png' },
  { name: 'ME/PMI', fullName: "Ministère de l'Eau et des Petites et Moyennes Industries", logo: null },
  { name: 'CAPEF', fullName: "Conseil Africain et Malgache pour l'Enseignement Supérieur", logo: '/images/partners/capef.png' },
];

// ─── Block 2: Tutelle Académique ─────────────────────────────────────────────
const tutelles = [
  { name: 'Université de Douala', abbr: 'UD', logo: '/images/partners/ud.png' },
  { name: 'Université de Bertoua', abbr: 'UBe', logo: '/images/partners/ube.png' },
  { name: 'École Nationale Supérieure Polytechnique de Douala', abbr: 'ENSPD', logo: '/images/partners/enspd.jpg' },
  { name: 'École Supérieure de Transformation des Mines et des Ressources Énergétiques', abbr: 'ESTMRE', logo: '/images/partners/estmre.jpg' },
  { name: "Institut Supérieur d'Agriculture, du Bois, de l'Eau et de l'Environnement", abbr: 'ISABEE', logo: '/images/partners/isabee.jpg' },
];

// ─── Block 3: Partenaires Privés ─────────────────────────────────────────────
const privés = [
  { name: 'OMPI / WIPO', type: 'International', logo: '/images/partners/ompi.jpg' },
  { name: 'BeingCert USA', type: 'Certification', logo: '/images/partners/beingcert.jpg' },
  { name: 'CRDI Canada', type: 'Research', logo: '/images/partners/crdi.jpg' },
  { name: 'ISABE Europe', type: 'Business', logo: '/images/partners/isabe.jpg' },
  { name: 'Velsoft Canada', type: 'Technology', logo: '/images/partners/velsoft.png' },
  { name: 'QH India', type: 'Quantum Research', logo: '/images/partners/qh-india.png' },
  { name: 'Afreetech', type: 'Technology', logo: '/images/partners/afreetech.png' },
  { name: 'Bee-Learning', type: 'E-Learning', logo: '/images/partners/bee-learning.jpg' },
  { name: 'Clickom', type: 'Communication', logo: '/images/partners/clickom.jpg' },
  { name: 'RIIS', type: 'Innovation', logo: '/images/partners/riis.jpg' },
  { name: 'Mbam E-learning', type: 'Education', logo: '/images/partners/mbam-elearning.jpg' },
  { name: 'PACY', type: 'Youth Development', logo: '/images/partners/pacy.png' },
  { name: 'Polytech Douala', type: 'Engineering', logo: '/images/partners/polytech.jpg' },
];

// ─── Block 4: Partenaires de l'ANIPES ────────────────────────────────────────
const anipes = [
  { name: 'ANIPES', fullName: "Association Nationale pour l'Inclusion et la Promotion de l'Enseignement Supérieur", logo: '/images/partners/anipes.png' },
  { name: 'ESBAFIM', fullName: "École Supérieure de Business, d'Administration, de Finance et d'Ingénierie du Management", logo: '/images/partners/esbafim.jpg' },
];

function PartnerLogoCard({ name, logo, sub }: { name: string; logo: string | null; sub?: string }) {
  return (
    <div className="bg-gray-50 p-6 flex flex-col items-center justify-center text-center hover:bg-white hover:shadow-xl hover:border-gold transition-all duration-500 rounded-sm group border border-transparent min-h-[120px]">
      <div className="h-14 w-full mb-4 flex items-center justify-center">
        {logo ? (
          <img src={logo} alt={name} className="max-h-14 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-navy/40" />
          </div>
        )}
      </div>
      <h3 className="text-[10px] md:text-xs font-black text-navy uppercase tracking-tighter leading-tight line-clamp-2">{name}</h3>
      {sub && <div className="text-gold text-[8px] font-black uppercase tracking-widest mt-1">{sub}</div>}
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b-2 border-gray-100 pb-6">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center text-gold flex-shrink-0">
          {icon}
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic leading-none">{title}</h2>
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

export default function Partnerships() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={t('partnerships_title', 'Partenariats & Relations Internationales')}
        description={t('partnerships_subtitle', "Un réseau mondial au service de l'excellence académique d'ESTAD.")}
        breadcrumbs={[{ label: t('nav_partnerships', 'Partnerships'), href: '/partnerships' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-20 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero Banner Image */}
          <div className="w-full h-48 md:h-72 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold group relative">
            <img src="/images/campus/doc-event-02.jpg" alt="Partnerships" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-navy/40" />
          </div>

          {/* ── Block 1: Institutionnels ── */}
          <section id="institutionnels">
            <SectionHeader
              icon={<Shield className="w-6 h-6" />}
              title="Partenaires Institutionnels"
              subtitle="Organismes gouvernementaux & institutions publiques"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mt-8">
              {institutionnels.map((p, idx) => (
                <PartnerLogoCard key={idx} name={p.name} logo={p.logo} sub={undefined} />
              ))}
            </div>
          </section>

          {/* ── Block 2: Tutelle Académique ── */}
          <section id="tutelle">
            <SectionHeader
              icon={<GraduationCap className="w-6 h-6" />}
              title="Tutelle Académique"
              subtitle="Universités & grandes écoles superviseurs"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mt-8">
              {tutelles.map((t, idx) => (
                <div key={idx} className="bg-gray-50 p-6 flex flex-col items-center justify-center text-center hover:bg-white hover:shadow-xl hover:border-gold transition-all duration-500 rounded-sm group border border-transparent">
                  <div className="h-16 w-full mb-4 flex items-center justify-center">
                    <img
                      src={t.logo}
                      alt={t.name}
                      className="max-h-16 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-gold text-[9px] font-black uppercase tracking-widest mb-1">{t.abbr}</div>
                  <h3 className="text-[9px] font-black text-navy uppercase tracking-tighter leading-tight line-clamp-3">{t.name}</h3>
                </div>
              ))}
            </div>
          </section>

          {/* ── Block 3: Partenaires Privés ── */}
          <section id="privés">
            <SectionHeader
              icon={<Globe className="w-6 h-6" />}
              title="Partenaires Privés"
              subtitle="Entreprises, ONG et organisations internationales"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-8">
              {privés.map((p, idx) => (
                <PartnerLogoCard key={idx} name={p.name} logo={p.logo} sub={p.type} />
              ))}
            </div>
          </section>

          {/* ── Block 4: Partenaires de l'ANIPES ── */}
          <section id="anipes">
            <SectionHeader
              icon={<Heart className="w-6 h-6" />}
              title="Partenaires de l'ANIPES"
              subtitle="Réseau d'inclusion et de promotion de l'enseignement supérieur"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
              {anipes.map((p, idx) => (
                <div key={idx} className="bg-gray-50 p-8 border border-gray-100 hover:bg-white hover:shadow-xl hover:border-gold transition-all duration-500 rounded-sm group flex flex-col md:flex-row items-center gap-6">
                  <div className="h-20 w-32 flex items-center justify-center flex-shrink-0">
                    <img
                      src={p.logo}
                      alt={p.name}
                      className="max-h-20 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-center md:text-left space-y-1">
                    <h3 className="text-xl font-black text-navy uppercase tracking-tight leading-none group-hover:text-gold transition-colors">{p.name}</h3>
                    <p className="text-gray-500 text-xs font-medium leading-relaxed">{p.fullName}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── International Reach Banner ── */}
          <section id="reach" className="bg-navy p-8 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000 pointer-events-none"></div>
            <div className="relative z-10 text-center space-y-8">
              <span className="inline-block px-4 py-1.5 text-[10px] font-black text-gold border-2 border-gold/30 rounded-full tracking-widest uppercase">
                Rayonnement International
              </span>
              <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tighter italic leading-tight">
                {t('partnerships_map_title', 'Un Réseau Mondial')}
              </h2>
              <p className="text-lg md:text-xl font-light text-white/80 leading-relaxed italic max-w-3xl mx-auto">
                "{t('partnerships_quote', "Les partenariats ne sont pas des signatures sur papier — ce sont des ponts que nous construisons entre le talent camerounais et l'excellence mondiale.")}"
              </p>
              <div className="flex flex-wrap justify-center gap-6 md:gap-12 pt-8 border-t border-white/10">
                {['Europe', 'Canada', 'Inde', 'Asie', 'Afrique'].map((region) => (
                  <div key={region} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gold animate-pulse"></div>
                    <span className="font-black uppercase tracking-widest text-[10px] md:text-xs">{region}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-center pt-4">
                <Link to="/contact" className="bg-gold text-navy px-12 py-4 font-black uppercase tracking-widest text-xs shadow-xl hover:brightness-110 transition-all active:scale-95">
                  Devenir Partenaire
                </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
